import { createEntityAdapter, createSlice, EntityId } from '@reduxjs/toolkit';
import { DocumentsDataItemProps } from 'components/patient/Documents/documents.types';
import type { RootState } from 'store';

import { documentsApi } from './documents/documentsApi';
import { patientsApi } from './patientApi';
import { patientPaymentApi } from './patientPayment/patientPaymentApi';
import { PatientInitialStateProps } from './patients.types';

const documentsAdapter = createEntityAdapter<DocumentsDataItemProps, EntityId>({
  selectId: (document) => document._id,
  sortComparer: (a, b) => b.createdAt?.localeCompare(a.createdAt)
});

const initialState: PatientInitialStateProps = {
  benefits: null,
  documents: {
    items: documentsAdapter.getInitialState(),
    patientId: '',
    totalCount: 0
  },
  insurancePaymentProfiles: null,
  isTripleTherapyPatient: false,
  patientInfo: null,
  paymentProfiles: null
};

const patientSlice = createSlice({
  extraReducers: (builder) => {
    builder.addMatcher(
      documentsApi.endpoints.getPatientDocuments.matchFulfilled,
      (state, { meta, payload }) => {
        state.documents.totalCount = payload.info.totalCount;
        if (state.documents.patientId === meta.arg.originalArgs.patientId) {
          documentsAdapter.upsertMany(state.documents.items, payload.data);
        } else {
          state.documents.patientId = meta.arg.originalArgs.patientId;
          documentsAdapter.setAll(state.documents.items, payload.data);
        }
      }
    );
    builder.addMatcher(documentsApi.endpoints.deleteDocument.matchFulfilled, (state, { meta }) => {
      documentsAdapter.removeOne(state.documents.items, meta.arg.originalArgs.documentId);
    });
    builder.addMatcher(
      documentsApi.endpoints.uploadDocuments.matchFulfilled,
      (state, { payload }) => {
        documentsAdapter.upsertOne(state.documents.items, payload.data);
      }
    );
    builder.addMatcher(patientsApi.endpoints.getBenefits.matchFulfilled, (state, { payload }) => {
      state.benefits = payload;
    });
    builder.addMatcher(
      patientPaymentApi.endpoints.getPaymentProfile.matchFulfilled,
      (state, { payload }) => {
        if (!payload || !('paymentProfiles' in payload)) return;
        state.paymentProfiles = payload.paymentProfiles || null;
        state.insurancePaymentProfiles = payload.insurancePaymentProfiles || null;
      }
    );
    builder.addMatcher(patientsApi.endpoints.getPatient.matchFulfilled, (state, { payload }) => {
      state.patientInfo = payload;
    });
  },
  initialState,
  name: 'patient',
  reducers: {
    resetBenefits: (state) => {
      state.benefits = null;
    },
    resetDocuments: (state) => {
      documentsAdapter.removeAll(state.documents.items);
      state.documents.totalCount = 0;
      state.documents.patientId = '';
    },
    resetPatientInfo: (state) => {
      state.patientInfo = null;
    },
    resetPaymentProfiles: (state) => {
      state.paymentProfiles = null;
    },
    setIsTripleTherapyPatient: (state, action) => {
      state.isTripleTherapyPatient = action.payload;
    }
  }
});

export const patientDocumentsSelectors = documentsAdapter.getSelectors<RootState>(
  (state) => state.patient.documents.items
);

export const selectPatient = (state: RootState) => state.patient;

export const {
  resetBenefits,
  resetDocuments,
  resetPatientInfo,
  resetPaymentProfiles,
  setIsTripleTherapyPatient
} = patientSlice.actions;

export default patientSlice.reducer;
